import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';
import { Trophy, Loader2, AlertCircle } from 'lucide-react';

export const WkInviteRedirect = () => {
    const { inviteCode } = useParams();
    const { user, loading: authLoading } = useAuth();
    const navigate = useNavigate();
    
    const [status, setStatus] = useState('processing'); // 'processing', 'error', 'success'
    const [leagueName, setLeagueName] = useState('');
    const [errorMessage, setErrorMessage] = useState('');

    useEffect(() => {
        if (!authLoading && inviteCode) {
            processInvite();
        }
    }, [inviteCode, user, authLoading]);

    const processInvite = async () => {
        try {
            // 1. Verify the league exists
            const { data: league, error: leagueError } = await supabase
                .from('wk_leagues')
                .select('id, name')
                .eq('invite_code', inviteCode)
                .single();

            if (leagueError || !league) {
                setStatus('error');
                setErrorMessage('Ongeldige invite code. Deze poule bestaat niet.');
                return;
            }

            setLeagueName(league.name);

            // 2. If not logged in, send them to login first (preserving redirect)
            if (!user) {
                // Save destination in session storage or pass in query param
                // For simplicity, we'll store it and wait for login
                setStatus('login_required');
                return;
            }

            // 3. Try adding them to the league
            const { error: joinError } = await supabase
                .from('wk_league_members')
                .insert([{ league_id: league.id, user_id: user.id }]);

            // Handle duplicate membership silently
            if (joinError && joinError.code !== '23505') {
                throw joinError;
            }

            // 4. Success! Redirect directly to the league
            setStatus('success');
            setTimeout(() => {
                navigate(`/wkpoule/league/${league.id}`);
            }, 1500);

        } catch (err) {
            console.error('Error processing invite:', err);
            setStatus('error');
            setErrorMessage(err.message);
        }
    };

    return (
        <div className="min-h-screen bg-black text-white flex flex-col items-center justify-center p-6">
            <div className="max-w-md w-full bg-white/5 border border-white/10 rounded-3xl p-8 text-center relative overflow-hidden">
                <div className="absolute top-0 left-0 w-full h-2 bg-gradient-to-r from-orange-500 via-pink-500 to-red-500"></div>
                
                {status === 'processing' && (
                    <div className="animate-pulse py-8">
                        <div className="w-20 h-20 mx-auto bg-orange-500/20 rounded-full flex items-center justify-center mb-6">
                            <Loader2 size={40} className="animate-spin text-orange-500" />
                        </div>
                        <h2 className="text-xl font-bold">Lidmaatschap Verifiëren...</h2>
                    </div>
                )}

                {status === 'login_required' && (
                    <div className="py-6">
                        <div className="w-20 h-20 mx-auto bg-blue-500/20 rounded-full flex items-center justify-center mb-6">
                            <Trophy size={40} className="text-blue-500" />
                        </div>
                        <h2 className="text-2xl font-black mb-2">Je bent uitgenodigd!</h2>
                        <p className="text-gray-400 mb-8">Je bent uitgenodigd om lid te worden van <span className="text-white font-bold">"{leagueName}"</span>. Log in om deel te nemen.</p>
                        
                        <Link 
                            to={`/login?redirectTo=/wkpoule/join/${inviteCode}`} 
                            className="block w-full py-4 bg-orange-500 hover:bg-orange-600 text-white font-black rounded-xl transition-all text-lg text-center"
                        >
                            Nu Inloggen / Registreren
                        </Link>
                    </div>
                )}

                {status === 'success' && (
                    <div className="py-8">
                        <div className="w-20 h-20 mx-auto bg-green-500/20 rounded-full flex items-center justify-center mb-6">
                            <Trophy size={40} className="text-green-500" />
                        </div>
                        <h2 className="text-2xl font-black text-green-400 mb-2">Succesvol Toegevoegd!</h2>
                        <p className="text-gray-300">Je doet nu mee aan <span className="font-bold">"{leagueName}"</span>.</p>
                        <p className="text-sm text-gray-500 mt-4 animate-pulse">We sturen je nu door...</p>
                    </div>
                )}

                {status === 'error' && (
                    <div className="py-6">
                        <div className="w-20 h-20 mx-auto bg-red-500/20 rounded-full flex items-center justify-center mb-6">
                            <AlertCircle size={40} className="text-red-500" />
                        </div>
                        <h2 className="text-2xl font-black text-red-400 mb-2">Oeps...</h2>
                        <p className="text-gray-400 mb-8">{errorMessage}</p>
                        
                        <Link to="/wkpoule" className="text-orange-500 font-bold hover:underline">
                            Terug naar home
                        </Link>
                    </div>
                )}
            </div>
        </div>
    );
};
